/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PersonalizationSwapTagsMode } from '@btag/bt-personalization';
import { isEqual } from 'lodash';
import PreviewIframe from './PreviewIframe';
import PreviewLeftPanel from './PreviewLeftPanel';
import * as browserActions from '../../actions/browser';
import * as htmlOutputActions from '../../actions/htmlOutput';
import * as Styled from './styles';
import eventService from '../../common/eventService';
import { blockType } from '../../types';
import { request } from '../../common/request';
import { ERR_HTML_GENERATION } from '../../constants/Errors';
import { APPLICATION_MODES } from '../../constants/itemTypes/itemTypes';

// due to mvp deadline we will be sending the recipient data to create service instead of fetching there
const getPersonalizationRecipientData = (recipientID, singleUseTags) =>
    _map(singleUseTags, ({ name, fallback, previewData }) => ({
        tag: name,
        value: recipientID ? previewData[recipientID] : '',
        fallback,
    }));

const getPersonalizationRecipients = (singleUseTags) =>
    !_isEmpty(singleUseTags) ? Object.keys(Object.values(singleUseTags)[0]?.previewData) : [];

const getHtmlCachedOptionsWithRecipients = (options, recipients) => {
    const newOptions = { ...options };
    recipients.forEach((recipient) => {
        newOptions.VALUE[recipient] = { body: '', bodyWithCF: '' };
    });
    return newOptions;
};

const DEFAULT_CACHED_HTML_OPTIONS = {
    NONE: { body: '', bodyWithCF: '' },
    FALLBACK: { body: '', bodyWithCF: '' },
    VALUE: {},
};

const Preview = ({ blocks, htmlOutput, draftID, singleUseTags, dispatch, previewWidth, appMode }) => {
    const [useEmbeddedCustomFonts, setUseEmbeddedCustomFonts] = useState(false);
    const [personalizationTagsMode, setPersonalizationTagsMode] = useState(PersonalizationSwapTagsMode.NONE);
    const [personalizationRecipient, setPersonalizationRecipient] = useState('');
    const [cachedHtml, setCachedHtml] = useState(DEFAULT_CACHED_HTML_OPTIONS);
    const refBlock = useRef({});
    const isLoadingRef = useRef(false);

    const { ed563MailMergeV2 } = useFlags();

    useEffect(() => {
        if (ed563MailMergeV2 && _isEmpty(cachedHtml.VALUE)) {
            setCachedHtml(
                getHtmlCachedOptionsWithRecipients(
                    DEFAULT_CACHED_HTML_OPTIONS,
                    getPersonalizationRecipients(singleUseTags)
                )
            );
        }
    }, [ed563MailMergeV2, cachedHtml.VALUE, singleUseTags]);

    const loadHTML = async () => {
        if (isLoadingRef.current) return;
        
        isLoadingRef.current = true;
        
        const cachedHtmlUpdated = cachedHtml;

        const htmlChanged = !isEqual(blocks, refBlock.current.blocks);
        const personalizationChanged =
            personalizationRecipient &&
            !_isEmpty(singleUseTags) &&
            personalizationTagsMode === PersonalizationSwapTagsMode.VALUE &&
            !_isEmpty(cachedHtmlUpdated.VALUE) &&
            cachedHtmlUpdated.VALUE[personalizationRecipient].body === '';

        if (htmlChanged || cachedHtmlUpdated[personalizationTagsMode].body === '' || personalizationChanged) {
            let cachedHTMLNew = htmlChanged ? DEFAULT_CACHED_HTML_OPTIONS : cachedHtmlUpdated;

            if (ed563MailMergeV2 && htmlChanged) {
                cachedHTMLNew = getHtmlCachedOptionsWithRecipients(
                    DEFAULT_CACHED_HTML_OPTIONS,
                    getPersonalizationRecipients(singleUseTags)
                );
            }

            try {
                dispatch(htmlOutputActions.updatedLoadedStatus(false));

                const convertHtmlPayload = {
                    draftID,
                    blocks,
                    useEmbeddedCustomFonts: true,
                    personalizationTagsMode,
                };

                if (ed563MailMergeV2) {
                    convertHtmlPayload.personalizationRecipientData = getPersonalizationRecipientData(
                        personalizationRecipient,
                        singleUseTags
                    );
                }

                const { html, htmlWithEmbeddedCustomFonts } = await request.convertHtml(convertHtmlPayload);

                dispatch(
                    htmlOutputActions.updatedHtmlBody({
                        html,
                        htmlWithEmbeddedCustomFonts,
                        useEmbeddedCustomFonts: true,
                    })
                );

                if (personalizationTagsMode === PersonalizationSwapTagsMode.VALUE) {
                    cachedHTMLNew.VALUE[personalizationRecipient] = {
                        body: html,
                        bodyWithCF: htmlWithEmbeddedCustomFonts,
                    };
                    setCachedHtml(cachedHTMLNew);
                } else {
                    setCachedHtml({
                        ...cachedHTMLNew,
                        [personalizationTagsMode]: { body: html, bodyWithCF: htmlWithEmbeddedCustomFonts },
                    });
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                eventService.notify(err.customMessage ? err.customMessage : ERR_HTML_GENERATION, true);
                dispatch(browserActions.appModeChanged(APPLICATION_MODES.EDITOR));
            }
        }
        
        isLoadingRef.current = false;
    };

    useEffect(() => {
        if (
            appMode === APPLICATION_MODES.PREVIEW &&
            !isEqual(blocks, refBlock.current.blocks) &&
            !isLoadingRef.current
        ) {
            loadHTML();
            refBlock.current = { blocks };
        }
    }, [appMode, blocks]);

    useEffect(
        () => () => {
            dispatch(htmlOutputActions.updatedLoadedStatus(false));
        },
        [dispatch]
    );

    const { loaded, body, bodyWithEmbeddedCustomFonts } = htmlOutput;

    const renderBody = () => {
        if (
            personalizationTagsMode === PersonalizationSwapTagsMode.VALUE &&
            !!cachedHtml.VALUE?.[personalizationRecipient]?.body &&
            !!cachedHtml.VALUE?.[personalizationRecipient]?.bodyWithCF
        ) {
            return useEmbeddedCustomFonts
                ? cachedHtml.VALUE?.[personalizationRecipient]?.bodyWithCF
                : cachedHtml.VALUE?.[personalizationRecipient]?.body;
        }

        if (
            personalizationTagsMode !== PersonalizationSwapTagsMode.VALUE &&
            !!cachedHtml[personalizationTagsMode]?.body &&
            !!cachedHtml[personalizationTagsMode]?.bodyWithCF
        ) {
            return useEmbeddedCustomFonts
                ? cachedHtml[personalizationTagsMode]?.bodyWithCF
                : cachedHtml[personalizationTagsMode]?.body;
        }

        return useEmbeddedCustomFonts ? bodyWithEmbeddedCustomFonts : body;
    };

    return (
        <Styled.PreviewDiv>
            <Styled.LeftPanelOuterDiv>
                <PreviewLeftPanel
                    onCustomFontsRenderOptionsChange={(renderWithEmbeddedCustomFonts) =>
                        setUseEmbeddedCustomFonts(renderWithEmbeddedCustomFonts)
                    }
                    onPersonalizationModeChange={(mode) => setPersonalizationTagsMode(mode)}
                    onPersonalizationRecipientChange={(recipient) => setPersonalizationRecipient(recipient)}
                />
            </Styled.LeftPanelOuterDiv>
            <Styled.CenterPanelDiv>
                <Styled.LoadingDiv>
                    {!loaded && (
                        <>
                            <Styled.LoadingImg
                                width="200"
                                src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/banana-loader.gif"
                                alt="loader"
                            />
                            <Styled.LoadingText>Loading...</Styled.LoadingText>
                        </>
                    )}
                </Styled.LoadingDiv>
                <Styled.Preview>
                    <Styled.InnerDiv>
                        {loaded && <PreviewIframe body={renderBody()} previewWidth={previewWidth} />}
                    </Styled.InnerDiv>
                </Styled.Preview>
            </Styled.CenterPanelDiv>
        </Styled.PreviewDiv>
    );
};

const mapStateToProps = (state) => ({
    appMode: state.browser.appMode,
    blocks: state.canvasBlocks.present,
    htmlOutput: _get(state, 'htmlOutput', ''),
    draftID: _get(state, 'session.collaboration.id', ''),
    singleUseTags: _get(state, 'personalization.tag.tags.singleUse', {}),
});

Preview.propTypes = {
    appMode: PropTypes.string.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    dispatch: PropTypes.func.isRequired,
    htmlOutput: PropTypes.object.isRequired,
    draftID: PropTypes.string.isRequired,
    singleUseTags: PropTypes.object.isRequired,
    previewWidth: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(Preview);
